import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ACTIONS,
  ACTION_HINTS,
  ACTION_LABELS,
  createRetirementJobs,
  getCandidates,
  getRetired,
  previewRetirement,
  unretire,
} from '../../services/retirementApi';
import { SYNC_TARGETS } from '../../constants/markets';
import { useSession } from '../auth/SessionProvider';
import './ProductRetirement.css';

/*
 * Retire products.
 *
 * Group-search the legacy catalogue, pick a set of SKUs, then run a queued job
 * that unpublishes them and deletes their BigCommerce product. Deleting from BC
 * is irreversible, so the flow is deliberately three steps: search, review the
 * preview, then type DELETE to confirm.
 *
 * The target selector is (market, environment). Against a staging target the
 * backend only permits the BigCommerce-side actions, because Akeneo and the
 * product tables are shared with production — the boxes are disabled here to
 * match, rather than letting the operator tick something that gets silently
 * dropped server-side.
 */

const ACTION_ORDER = [
  ACTIONS.BLOCK_BC,
  ACTIONS.AKENEO_UNPUBLISH,
  ACTIONS.BC_DELETE,
  ACTIONS.CLEAR_MAPPING,
];

const CONFIRM_WORD = 'DELETE';

const emptyFilters = {
  skus: '',
  sku_prefix: '',
  q: '',
  brand: '',
  classification: '',
  lifecycle: '',
  market: '',
  only_live_in_bc: false,
  include_retired: false,
};

export default function ProductRetirement() {
  const { isAdmin } = useSession();

  const [targetId, setTargetId] = useState('staging:AU');
  const target = useMemo(
    () => SYNC_TARGETS.find((t) => t.id === targetId) || SYNC_TARGETS[0],
    [targetId],
  );

  const [filters, setFilters] = useState(emptyFilters);
  const [candidates, setCandidates] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [actions, setActions] = useState(new Set(ACTION_ORDER));
  const [allowed, setAllowed] = useState(ACTION_ORDER);
  const [reason, setReason] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [confirmText, setConfirmText] = useState('');

  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const setFilter = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFilters((f) => ({ ...f, [key]: value }));
  };

  // Changing the target invalidates a preview: the permitted action set and the
  // resolved BigCommerce ids are both environment-specific.
  useEffect(() => {
    setPreview(null);
    setResult(null);
  }, [targetId]);

  const search = useCallback(async () => {
    setBusy(true);
    setError(null);
    setPreview(null);
    setResult(null);
    try {
      const params = { environment: target.environment, limit: 200 };
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== '' && v !== false) params[k] = v;
      });
      const data = await getCandidates(params);
      setCandidates(data);
      setAllowed(data.allowed_actions || ACTION_ORDER);
      setActions((prev) => {
        const permitted = new Set(data.allowed_actions || ACTION_ORDER);
        return new Set([...prev].filter((a) => permitted.has(a)));
      });
      setSelected(new Set());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [filters, target.environment]);

  const loadLedger = useCallback(async () => {
    try {
      setLedger(await getRetired({ limit: 200 }));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, []);

  const toggleSku = (sku) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
    setPreview(null);
  };

  const toggleAction = (action) => {
    setActions((s) => {
      const next = new Set(s);
      if (next.has(action)) next.delete(action);
      else next.add(action);
      return next;
    });
    setPreview(null);
  };

  const body = () => ({
    skus: Array.from(selected),
    markets: [target.market],
    environment: target.environment,
    actions: Array.from(actions),
    reason: reason || null,
    dry_run: dryRun,
  });

  const runPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      setPreview(await previewRetirement(body()));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await createRetirementJobs(body());
      setResult(data);
      setConfirmText('');
      setPreview(null);
      setSelected(new Set());
      await loadLedger();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const removeBlock = async (row) => {
    setBusy(true);
    setError(null);
    try {
      await unretire(row.product_sku, row.market_code === '*' ? undefined : row.market_code);
      await loadLedger();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const deleting = actions.has(ACTIONS.BC_DELETE);
  // A real (non-dry-run) BigCommerce delete is the only thing that needs the
  // typed confirmation; everything else is recoverable.
  const needsConfirm = deleting && !dryRun;
  const canSubmit =
    selected.size > 0 &&
    actions.size > 0 &&
    !busy &&
    (!needsConfirm || confirmText === CONFIRM_WORD);

  if (!isAdmin) {
    return (
      <div className="oc-scope prt-page">
        <div className="prt-alert warn">
          Retiring products needs the admin role. Sign in with an administrator account.
        </div>
      </div>
    );
  }

  return (
    <div className="oc-scope prt-page">
      <header className="oc-header">
        <div className="oc-header-text">
          <h1 className="oc-title">Retire products</h1>
          <p className="oc-blurb">
            Search a group of legacy products, stop them publishing, and delete them out of
            BigCommerce. Deleting is irreversible — preview first.
          </p>
        </div>
      </header>

      {error && <div className="prt-alert error">{error}</div>}

      {/* ── 1. Target ── */}
      <section className="oc-frame prt-frame">
        <h2 className="prt-h2">1. Choose the target</h2>
        <div className="prt-targets">
          {SYNC_TARGETS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`oc-chip ${t.id === targetId ? 'on' : ''}`}
              onClick={() => setTargetId(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        {target.environment !== 'prod' && (
          <p className="prt-note">
            Staging target: only the BigCommerce actions run. Akeneo and the product tables are
            shared with production, so unpublishing from here would take live products down.
          </p>
        )}
      </section>

      {/* ── 2. Search ── */}
      <section className="oc-frame prt-frame">
        <h2 className="prt-h2">2. Find the products</h2>
        <div className="prt-grid">
          <label className="oc-field prt-field-wide">
            <span className="oc-field-label">Paste a SKU list (commas or new lines)</span>
            <textarea
              className="oc-input prt-textarea"
              value={filters.skus}
              onChange={setFilter('skus')}
              placeholder={'ZZ1289\nZZ1290, ZZ1291'}
              rows={3}
            />
          </label>
          <label className="oc-field">
            <span className="oc-field-label">SKU starts with</span>
            <input className="oc-input" value={filters.sku_prefix} onChange={setFilter('sku_prefix')} placeholder="ZZ, ZZ?9" />
          </label>
          <label className="oc-field">
            <span className="oc-field-label">Text (SKU or name)</span>
            <input className="oc-input" value={filters.q} onChange={setFilter('q')} placeholder="pump, 244*, 244?1" />
          </label>
          <label className="oc-field">
            <span className="oc-field-label">Brand</span>
            <input className="oc-input" value={filters.brand} onChange={setFilter('brand')} />
          </label>
          <label className="oc-field">
            <span className="oc-field-label">Epicor classification</span>
            <input
              className="oc-input"
              value={filters.classification}
              onChange={setFilter('classification')}
              placeholder="non_stock_items"
            />
          </label>
          <label className="oc-field">
            <span className="oc-field-label">Lifecycle</span>
            <input className="oc-input" value={filters.lifecycle} onChange={setFilter('lifecycle')} />
          </label>
          <label className="oc-field">
            <span className="oc-field-label">Market membership</span>
            <select className="oc-input" value={filters.market} onChange={setFilter('market')}>
              <option value="">Any</option>
              <option value="AU">AU</option>
              <option value="NZ">NZ</option>
            </select>
          </label>
        </div>
        <div className="prt-toggles">
          <label>
            <input type="checkbox" checked={filters.only_live_in_bc} onChange={setFilter('only_live_in_bc')} />{' '}
            Only products still live in BigCommerce
          </label>
          <label>
            <input type="checkbox" checked={filters.include_retired} onChange={setFilter('include_retired')} />{' '}
            Include already-retired products
          </label>
          <button type="button" className="oc-btn oc-btn-primary" onClick={search} disabled={busy}>
            {busy ? 'Searching…' : 'Search'}
          </button>
        </div>
      </section>

      {/* ── 3. Select ── */}
      {candidates && (
        <section className="oc-frame prt-frame">
          <div className="oc-resultbar">
            <span className="oc-result-count">
              <strong>{selected.size}</strong> of {candidates.total} selected
              {candidates.truncated && ' (results truncated — narrow the search)'}
            </span>
            <span className="prt-bulk">
              <button
                type="button"
                className="oc-btn oc-btn-secondary"
                onClick={() => setSelected(new Set(candidates.items.map((p) => p.sku)))}
              >
                Select all
              </button>
              <button type="button" className="oc-btn oc-btn-secondary" onClick={() => setSelected(new Set())}>
                Clear
              </button>
            </span>
          </div>
          {candidates.items.length === 0 ? (
            <p className="prt-empty">No products matched.</p>
          ) : (
            <div className="oc-table-wrap prt-table-wrap">
              <table className="oc-table prt-table">
                <thead>
                  <tr>
                    <th className="prt-col-check" />
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Brand</th>
                    <th>Classification</th>
                    <th>AU BC id</th>
                    <th>NZ BC id</th>
                    <th>Publish</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.items.map((p) => (
                    <tr key={p.sku} className={selected.has(p.sku) ? 'on' : ''}>
                      <td className="prt-col-check">
                        <input type="checkbox" checked={selected.has(p.sku)} onChange={() => toggleSku(p.sku)} />
                      </td>
                      <td>
                        <code>{p.sku}</code>
                        {p.already_retired && <span className="prt-tag">retired</span>}
                      </td>
                      <td className="prt-name">{p.product_name || p.name || '—'}</td>
                      <td>{p.brand || '—'}</td>
                      <td>{p.epicor_classification || '—'}</td>
                      <td>{p.au_bc_id ?? '—'}</td>
                      <td>{p.nz_bc_id ?? '—'}</td>
                      <td>
                        {p.pd_publish ? 'global' : 'off'}
                        {p.au_publish ? ' · AU' : ''}
                        {p.nz_publish ? ' · NZ' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ── 4. Actions ── */}
      <section className="oc-frame prt-frame">
        <h2 className="prt-h2">3. Choose what happens</h2>
        <div className="prt-actions">
          {ACTION_ORDER.map((action) => {
            const permitted = allowed.includes(action);
            return (
              <label key={action} className={`prt-action ${permitted ? '' : 'off'}`}>
                <input
                  type="checkbox"
                  checked={actions.has(action)}
                  disabled={!permitted}
                  onChange={() => toggleAction(action)}
                />
                <span className="prt-action-text">
                  <span className="prt-action-label">
                    {ACTION_LABELS[action]}
                    {action === ACTIONS.BC_DELETE && <span className="prt-danger"> irreversible</span>}
                  </span>
                  <span className="prt-action-hint">
                    {permitted
                      ? ACTION_HINTS[action]
                      : `Not available against ${target.label} — this state is shared with production.`}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
        <label className="oc-field prt-field-wide">
          <span className="oc-field-label">Reason (stored on the ledger row)</span>
          <input
            className="oc-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Discontinued, superseded by X"
          />
        </label>
        <label className="prt-dry">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} /> Dry run (report
          what would happen, change nothing)
        </label>
      </section>

      {/* ── 5. Preview and run ── */}
      <section className="oc-frame prt-frame">
        <h2 className="prt-h2">4. Preview and run</h2>
        <div className="prt-runbar">
          <button
            type="button"
            className="oc-btn oc-btn-secondary"
            onClick={runPreview}
            disabled={busy || selected.size === 0 || actions.size === 0}
          >
            Preview
          </button>
          <button
            type="button"
            className={`oc-btn ${needsConfirm ? 'prt-btn-danger' : 'oc-btn-primary'}`}
            onClick={submit}
            disabled={!canSubmit}
          >
            {dryRun ? 'Queue dry run' : `Retire ${selected.size} product${selected.size === 1 ? '' : 's'}`}
          </button>
          {needsConfirm && (
            <label className="prt-confirm">
              <span>
                Type <code>{CONFIRM_WORD}</code> to confirm
              </span>
              <input
                className="oc-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
              />
            </label>
          )}
        </div>

        {preview && (
          <div className="prt-preview">
            <ul className="prt-facts">
              <li>
                <strong>{preview.sku_count}</strong> SKU{preview.sku_count === 1 ? '' : 's'} against{' '}
                <strong>{target.label}</strong>
              </li>
              {Object.entries(preview.per_market).map(([market, stats]) => (
                <li key={market}>
                  {market}: <strong>{stats.with_bc_id}</strong> with a BigCommerce product,{' '}
                  {stats.without_bc_id} without
                </li>
              ))}
              <li>Actions that will run: {preview.actions_applied.join(', ') || 'none'}</li>
              {preview.actions_dropped.length > 0 && (
                <li className="prt-warn">
                  Dropped for this environment: {preview.actions_dropped.join(', ')}
                </li>
              )}
              {preview.missing_skus.length > 0 && (
                <li className="prt-warn">
                  Not found in the catalogue: {preview.missing_skus.join(', ')}
                </li>
              )}
              {preview.already_retired.length > 0 && (
                <li>Already retired (will be re-armed): {preview.already_retired.join(', ')}</li>
              )}
            </ul>
            {preview.shared_bc_id_conflicts.length > 0 && (
              <div className="prt-alert warn">
                <strong>{preview.bc_deletes_skipped} BigCommerce delete(s) will be skipped.</strong> These
                products are also pointed at by SKUs that are not being retired; deleting them would break
                those listings. The retired SKUs still get unpublished and blocked.
                <ul className="prt-conflicts">
                  {preview.shared_bc_id_conflicts.map((c) => (
                    <li key={`${c.market}-${c.bc_product_id}`}>
                      {c.market} product <code>{c.bc_product_id}</code> is shared with{' '}
                      {c.shared_with.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="prt-alert ok">
            Queued {result.jobs.length} job{result.jobs.length === 1 ? '' : 's'} for {result.sku_count} SKU
            {result.sku_count === 1 ? '' : 's'}
            {result.dry_run && ' (dry run)'}:{' '}
            {result.jobs.map((j, i) => (
              <React.Fragment key={j.job_id}>
                {i > 0 && ', '}
                <Link to="/jobs">
                  #{j.job_id} ({j.market})
                </Link>
              </React.Fragment>
            ))}
            . Progress and per-SKU results are on the jobs dashboard.
          </div>
        )}
      </section>

      {/* ── Ledger ── */}
      <section className="oc-frame prt-frame">
        <div className="oc-resultbar">
          <span className="oc-result-count">Retirement ledger</span>
          <button type="button" className="oc-btn oc-btn-secondary" onClick={loadLedger} disabled={busy}>
            {ledger ? 'Refresh' : 'Load'}
          </button>
        </div>
        {ledger && (
          ledger.items.length === 0 ? (
            <p className="prt-empty">Nothing is currently retired.</p>
          ) : (
            <div className="oc-table-wrap prt-table-wrap">
              <table className="oc-table prt-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Market</th>
                    <th>Reason</th>
                    <th>Retired by</th>
                    <th>When</th>
                    <th className="prt-col-act" />
                  </tr>
                </thead>
                <tbody>
                  {ledger.items.map((row) => (
                    <tr key={row.id}>
                      <td><code>{row.product_sku}</code></td>
                      <td className="prt-name">{row.product_name || '—'}</td>
                      <td>{row.market_code === '*' ? 'All' : row.market_code}</td>
                      <td className="prt-name">{row.reason || '—'}</td>
                      <td>{row.retired_by}</td>
                      <td>{row.retired_at ? new Date(row.retired_at).toLocaleDateString() : '—'}</td>
                      <td className="prt-col-act">
                        <button
                          type="button"
                          className="oc-btn oc-btn-secondary"
                          onClick={() => removeBlock(row)}
                          disabled={busy}
                          title="Lifts the block so the SKU can sync again. It does not re-publish it or restore the deleted BigCommerce product."
                        >
                          Un-retire
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </section>
    </div>
  );
}
